import React, { useEffect, useState, useRef } from "react";
import Popup from "reactjs-popup";
import SignatureCanvas from "react-signature-canvas";
import { useNavigate, useSearchParams } from "react-router-dom";
import "reactjs-popup/dist/index.css";
import { FaTimesCircle } from "react-icons/fa";
import { registerLocale } from "react-datepicker";
import { et } from "date-fns/locale";
import {
  fetchMeterBrands,
  fetchQnoms,
  fetchDiameters,
  fetchMeterReplacementDraft,
  updateMeterReplacementDraft,
} from "../../data/fetchData";
import { useNotification } from "../../context/NotificationContext";
import { formatDecimal } from "../../utils/formatters";

registerLocale("et", et);

const TYPE_LABELS = {
  MAIN_GAUGE: "Põhiarvesti",
  SUB_GAUGE: "Alamarvesti",
  WATERING: "Kastmine",
};

export default function Replace2() {
  const [data, setData] = useState({});
  const [brandOptions, setBrandOptions] = useState([]);
  const [qnOptions, setQnOptions] = useState([]);
  const [diamOptions, setDiamOptions] = useState([]);
  const sigCanvas = useRef({});
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [searchParams] = useSearchParams();
  const draftParam = searchParams.get("draft");
  const draftId = draftParam ? Number(draftParam) : null;

  useEffect(() => {
    let isMounted = true;

    const loadDraftAndLookups = async () => {
      if (!draftId) {
        showError("Vahetuse mustandi ID puudub.");
        navigate("/Replace1");
        return;
      }

      try {
        const draft = await fetchMeterReplacementDraft(draftId);
        if (isMounted && draft?.payload) {
          setData(draft.payload);
        }
      } catch (err) {
        console.error("Failed to fetch replacement draft:", err);
        showError("Mustandi andmete laadimine ebaõnnestus.");
        return;
      }

      try {
        const [brands, qnoms, diams] = await Promise.all([
          fetchMeterBrands(),
          fetchQnoms(),
          fetchDiameters(),
        ]);
        if (!isMounted) return;
        setBrandOptions(brands);
        setQnOptions(qnoms);
        setDiamOptions(diams);
      } catch (err) {
        console.error("Failed to load lookup tables:", err);
      }
    };

    loadDraftAndLookups();

    return () => {
      isMounted = false;
    };
  }, [draftId, navigate, showError]);

  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("et-EE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  const actionLabel = data.workType || "—";
  const typeLabel = TYPE_LABELS[data.meterType] || data.meterType || "—";

  const oldBrandName = (() => {
    const b = brandOptions.find((b) => b.id === Number(data.oldBrand));
    return b ? b.name : data.oldBrand || "—";
  })();

  const newBrandName = (() => {
    const b = brandOptions.find((b) => b.id === Number(data.veemootjaMark));
    return b ? b.name : data.veemootjaMark || "—";
  })();

  const oldQNomLabel = (() => {
    const q = qnOptions.find((q) => q.id === Number(data.oldQn));
    return q ? formatDecimal(q.q_nom) : data.oldQn || "—";
  })();

  const newQNomLabel = (() => {
    const q = qnOptions.find((q) => q.id === Number(data.qn));
    return q ? formatDecimal(q.q_nom) : data.qn || "—";
  })();

  const oldDiamLabel = (() => {
    const d = diamOptions.find((d) => d.id === Number(data.oldDiameter));
    return d ? formatDecimal(d.diameter) : data.oldDiameter || "—";
  })();

  const diamLabel = (() => {
    const d = diamOptions.find((d) => d.id === Number(data.diameter));
    return d ? formatDecimal(d.diameter) : data.diameter || "—";
  })();

  const handleBackEdit = () => {
    if (draftId) {
      navigate(`/Replace1?draft=${draftId}`);
    } else {
      navigate(-1);
    }
  };

  const clearSig = () => {
    sigCanvas.current.clear();
  };

  const saveAndNext = async () => {
    if (!validateBeforeSign()) return;
    if (!draftId) {
      showError("Mustandi ID puudub, salvestamine ebaõnnestus.");
      return;
    }
    const signature = sigCanvas.current
      .getTrimmedCanvas()
      .toDataURL("image/png");
    const combinedData = {
      ...data,
      signature,
      draftSavedAt: new Date().toISOString(),
    };

    try {
      const updated = await updateMeterReplacementDraft(draftId, combinedData);
      if (updated?.payload) {
        setData(updated.payload);
      }
      navigate(`/Replace3?draft=${draftId}`);
    } catch (error) {
      console.error("Failed to update replacement draft with signature:", error);
      showError("Allkirjastatud mustandi salvestamine ebaõnnestus.");
    }
  };

  const validateBeforeSign = () => {
    // Field names that must be present (notes excluded)
    const required = [
      "oldMeterNumber",
      "oldReading",
      "tehaseNumber",
      "arvestiNait",
      "estate",
      "client",
      "veemootjaMark",
      "qn",
      "diameter",
      "meterType",
      "sealNumber",
      "workDate",
      "viimaneTaatlemine",
      "sealDate",
    ];

    for (const key of required) {
      const val = data[key];
      if (
        val === undefined ||
        val === null ||
        (typeof val === "string" && val.trim() === "")
      ) {
        showError("Kõik lahtrid (v.a märkus) peavad olema täidetud enne allkirjastamist.");
        return false;
      }
    }
    return true;
  };

  return (
    <div className="max-w-4xl mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg border border-gray-300">
      <div className="bg-primary text-white text-center py-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Vahetuse kokkuvõte</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Old meter section */}
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h2 className="text-lg font-semibold text-red-800 mb-4">
            Eemaldatav arvesti
          </h2>
          <table className="w-full table-auto">
            <tbody>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Arvesti number
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.oldMeterNumber || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Aadress
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.oldAddress || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Lepingupartner
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.oldOwner || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Arvesti mark
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {oldBrandName}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Qn/m³
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {oldQNomLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Läbimõõt
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {oldDiamLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Viimane näit
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {formatDecimal(data.oldReading) || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Viimane taatlemine
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {fmtDate(data.oldLastCertification)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* New meter section */}
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h2 className="text-lg font-semibold text-green-800 mb-4">
            Paigaldatav arvesti
          </h2>
          <table className="w-full table-auto">
            <tbody>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Arvesti number
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.tehaseNumber || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Kuupäev
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {fmtDate(data.workDate)}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Töö liik
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {actionLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Tüüp
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {typeLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Arvesti mark
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {newBrandName}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Qn/m³
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {newQNomLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Läbimõõt
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {diamLabel}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Algseisu näit
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {formatDecimal(data.arvestiNait) || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Viimane taatlemine
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {fmtDate(data.viimaneTaatlemine)}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Plommimise kuupäev
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {fmtDate(data.sealDate)}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Plommi number
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.sealNumber || "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Konsoolil
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.meterIsOnConsole ?? "—"}
                </td>
              </tr>
              <tr>
                <th className="p-2 text-left border-t border-gray-200 text-sm">
                  Mõõdusõlm vastavuses
                </th>
                <td className="p-2 border-t border-gray-200 text-sm">
                  {data.meterNodeCompliant || "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional details */}
      <table className="w-full table-auto mt-6">
        <tbody>
          {data.notes && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200">
                Märkused
              </th>
              <td className="p-2 border-t border-gray-200">{data.notes}</td>
            </tr>
          )}
          {/* signature */}
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Allkiri</th>
            <td className="p-2 border-t border-gray-200">
              {data.signature ? (
                <Popup
                  modal
                  trigger={
                    <img
                      src={data.signature}
                      alt="Signature"
                      className="w-24 h-auto object-cover rounded-md cursor-pointer"
                    />
                  }
                  closeOnDocumentClick={false}
                >
                  {(close) => (
                    <div className="relative p-4">
                      <img
                        src={data.signature}
                        alt="Signature"
                        className="w-full h-auto max-w-3xl mx-auto"
                      />
                      <button
                        onClick={close}
                        className="absolute top-2 right-2 text-2xl"
                      >
                        <FaTimesCircle />
                      </button>
                    </div>
                  )}
                </Popup>
              ) : (
                <em>Pole allkirja</em>
              )}
            </td>
          </tr>
          {/* old meter images */}
          {data.oldImages && data.oldImages.length > 0 && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200">
                Vana arvesti pildid
              </th>
              <td className="p-2 border-t border-gray-200">
                <div className="flex space-x-4 flex-wrap">
                  {data.oldImages.map((img, idx) => (
                    <Popup
                      key={`old-${idx}`}
                      modal
                      trigger={
                        <img
                          src={img}
                          alt={`Vana arvesti pilt ${idx + 1}`}
                          className="w-24 h-24 object-cover rounded-md cursor-pointer border border-red-200"
                        />
                      }
                      closeOnDocumentClick={false}
                    >
                      {(close) => (
                        <div className="relative p-4">
                          <img
                            src={img}
                            alt={`Vana arvesti pilt ${idx + 1}`}
                            className="w-full h-auto max-w-3xl mx-auto"
                          />
                          <button
                            onClick={close}
                            className="absolute top-2 right-2 text-2xl"
                          >
                            <FaTimesCircle />
                          </button>
                        </div>
                      )}
                    </Popup>
                  ))}
                </div>
              </td>
            </tr>
          )}
          {/* new meter images */}
          {data.newImages && data.newImages.length > 0 && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200">
                Uue arvesti pildid
              </th>
              <td className="p-2 border-t border-gray-200">
                <div className="flex space-x-4 flex-wrap">
                  {data.newImages.map((img, idx) => (
                    <Popup
                      key={`new-${idx}`}
                      modal
                      trigger={
                        <img
                          src={img}
                          alt={`Uue arvesti pilt ${idx + 1}`}
                          className="w-24 h-24 object-cover rounded-md cursor-pointer border border-green-200"
                        />
                      }
                      closeOnDocumentClick={false}
                    >
                      {(close) => (
                        <div className="relative p-4">
                          <img
                            src={img}
                            alt={`Uue arvesti pilt ${idx + 1}`}
                            className="w-full h-auto max-w-3xl mx-auto"
                          />
                          <button
                            onClick={close}
                            className="absolute top-2 right-2 text-2xl"
                          >
                            <FaTimesCircle />
                          </button>
                        </div>
                      )}
                    </Popup>
                  ))}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex justify-between mt-6">
        <button
          onClick={handleBackEdit}
          className="px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-hover"
        >
          Tagasi
        </button>
        <div className="flex gap-4">
          <button
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            onClick={async () => {
              if (!draftId) {
                showError("Mustandi ID puudub, salvestamine ebaõnnestus.");
                return;
              }
              const combined = {
                ...data,
                signature: null,
                draftSavedAt: new Date().toISOString(),
              };
              try {
                const updated = await updateMeterReplacementDraft(draftId, combined);
                if (updated?.payload) {
                  setData(updated.payload);
                }
                navigate(`/Replace3?draft=${draftId}`);
              } catch (error) {
                console.error("Failed to save unsigned draft:", error);
                showError("Mustandi salvestamine ebaõnnestus.");
              }
            }}
          >
            Saada allkirjata
          </button>
          <Popup
            modal
            trigger={
              <button className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover">
                Allkirjasta
              </button>
            }
            closeOnDocumentClick={true}
            overlayStyle={{ backgroundColor: "rgba(0,0,0,0.6)" }}
            contentStyle={{ maxWidth: "90%", width: "1200px", margin: "auto" }}
          >
            {(close) => (
              <div className="p-4">
                <div className="bg-white p-6 rounded-md shadow-lg relative">
                  <SignatureCanvas
                    ref={sigCanvas}
                    minWidth={1}
                    maxWidth={5}
                    canvasProps={{
                      className:
                        "signatureCanvas border border-gray-300 rounded-md",
                      style: { width: "100%", height: "60vh" },
                    }}
                  />
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={close}
                      className="text-2xl font-bold text-[var(--color-secondary)] hover:text-[var(--color-secondary-hover)] bg-white rounded-full p-1"
                    >
                      <FaTimesCircle size={40} />
                    </button>
                  </div>
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={clearSig}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    >
                      Tühjenda
                    </button>
                    <button
                      onClick={saveAndNext}
                      className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover"
                    >
                      Salvesta
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Popup>
        </div>
      </div>
    </div>
  );
}
